<template>
  <settings-modal
    class="forex-currency-pair-delete-modal t-forex-currency-pair-delete-modal"
  >
    <span
      slot="heading"
      class="font-weight-bold"
    >Delete currency pair</span>

    <data-row class="mb-2">
      <v-flex>
        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Currency pair</data-label>
          <forex-currency-pair-currencies
            :from="currencyPair.fromCurrency"
            :to="currencyPair.toCurrency"
          />
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Buy {{ currencyPair.fromCurrency }}</data-label>
          <div class="t-buy-rate">{{ currencyPair.buy.rate | number }}</div>
        </one-data-column>

        <one-data-column
          style="margin-bottom: 14px"
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Sell {{ currencyPair.toCurrency }}</data-label>
          <div class="t-sell-rate">{{ currencyPair.sell.rate | number }}</div>
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Fees buy</data-label>
          <div class="t-buy-fees">{{ currencyPair.buy.fee ? 'Enabled' : 'Disabled' }}</div>
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Fees sell</data-label>
          <div class="t-sell-fees">{{ currencyPair.sell.fee ? 'Enabled' : 'Disabled' }}</div>
        </one-data-column>

        <one-data-column>
          <data-heading
            slot="label"
            heading="Day"
            spaced
          />
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Margin buy</data-label>
          <div class="t-buy-day-margin">{{ formatPeriodMargin(currencyPair.buy.dayMargin, currencyPair.buy.dayMarginEnabled) }}</div>
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Margin sell</data-label>
          <div class="t-sell-day-margin">{{ formatPeriodMargin(currencyPair.sell.dayMargin, currencyPair.sell.dayMarginEnabled) }}</div>
        </one-data-column>

        <one-data-column>
          <data-heading
            slot="label"
            heading="Night"
            spaced
          />
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Margin buy</data-label>
          <div class="t-buy-night-margin">{{ formatPeriodMargin(currencyPair.buy.nightMargin, currencyPair.buy.nightMarginEnabled) }}</div>
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Margin sell</data-label>
          <div class="t-sell-night-margin">{{ formatPeriodMargin(currencyPair.sell.nightMargin, currencyPair.sell.nightMarginEnabled) }}</div>
        </one-data-column>

        <one-data-column>
          <data-heading
            slot="label"
            heading="Weekends"
            spaced
          />
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Margin buy</data-label>
          <div class="t-buy-weekends-margin">{{ formatPeriodMargin(currencyPair.buy.weekendMargin, currencyPair.buy.weekendMarginEnabled) }}</div>
        </one-data-column>

        <one-data-column
          :label-props="columnLabelProps"
          :data-props="columnDataProps"
        >
          <data-label slot="label">Margin sell</data-label>
          <div class="t-sell-weekends-margin">{{ formatPeriodMargin(currencyPair.sell.weekendMargin, currencyPair.sell.weekendMarginEnabled) }}</div>
        </one-data-column>
      </v-flex>
    </data-row>

    <template slot="actions">
      <v-btn
        class="t-confirm-button"
        color="error"
        medium
        flat
        :disabled="loading"
        @click="onDeleteButtonClick"
      >delete pair</v-btn>

      <v-btn
        class="t-cancel-button"
        color="primary"
        medium
        flat
        @click="onCloseButtonClick"
      >cancel</v-btn>
    </template>
  </settings-modal>
</template>

<script>
  // Events
  import { EVENT_FOREX_CURRENCY_PAIR_CHANGE } from '@/events/forexCurrencyPairs'

  // Services
  import ApiRequest from '@/services/ApiRequests/ApiRequest'
  import ForexCurrencyRatesApi from '@/services/Api/ForexCurrencyRatesApi'
  import ProfileApi from '@/services/Api/ProfileApi'

  // Mixins
  import modal from '@/mixins/modal'
  import flashMessages from '@/mixins/flashMessages'

  // Components
  import SettingsModal from '@/components/modals/SettingsModal'
  import DataHeading from '@/components/ui/Data/DataHeading'
  import DataRow from '../ui/Data/DataRow'
  import DataLabel from '@/components/ui/Data/DataLabel'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn'
  import ForexCurrencyPairCurrencies from '@/components/forex/ForexCurrencyPairCurrencies'

  export default {
    name: 'ForexCurrencyPairDeleteModal',
    components: {
      SettingsModal,
      DataHeading,
      DataRow,
      DataLabel,
      OneDataColumn,
      ForexCurrencyPairCurrencies
    },
    mixins: [modal, flashMessages],
    data () {
      return {
        deleteCurrencyPairApiRequest: null
      }
    },
    computed: {
      requestArguments () {
        const requestArguments = [
          this.currencyPair.fromCurrency,
          this.currencyPair.toCurrency
        ]

        if (this.modalData.profileId != null) {
          requestArguments.unshift(this.modalData.profileId)
        }

        return requestArguments
      },
      currencyPair () {
        return this.modalData.currencyPair
      },
      columnLabelProps () {
        return { style: 'width: 150px;' }
      },
      columnDataProps () {
        return { style: 'min-width: auto;' }
      },
      loading () {
        return this.deleteCurrencyPairApiRequest.loading
      }
    },
    created () {
      this.setDeleteCurrencyPairApiRequest(
        new ApiRequest(
          this.modalData.profileId != null
            ? ProfileApi.deleteForexCurrencyPair
            : ForexCurrencyRatesApi.deletePair
        )
      )
    },
    methods: {
      formatPeriodMargin (marginPeriodValue, marginPeriodEnabled) {
        return marginPeriodEnabled ? `${this.$options.filters.number(marginPeriodValue)}%` : 'N/A'
      },
      onDeleteButtonClick () {
        this.deleteCurrencyPairApiRequest
          .execute(...this.requestArguments)
          .then(() => {
            this.$eventHub.$emit(EVENT_FOREX_CURRENCY_PAIR_CHANGE)

            this.showSuccessFlashMessage('Currency pair has been successfully deleted.')
          })
          .catch(() => {
            this.showErrorFlashMessage('Currency pair has not been deleted.')
          })
      },
      onCloseButtonClick () {
        this.closeModal()
      },
      setDeleteCurrencyPairApiRequest (deleteCurrencyPairApiRequest) {
        this.$set(this, 'deleteCurrencyPairApiRequest', deleteCurrencyPairApiRequest)
      }
    }
  }
</script>
